import type { MarketingMotion } from "../../types";

export const SHARE_CONTROL_ORDER = ["save", "copy", "x", "telegram", "instagram", "reddit"] as const;
export type ShareControl = (typeof SHARE_CONTROL_ORDER)[number];
export type ShareSequencePhase = "heading" | "reading" | "opening" | "jump" | "settling"
  | "capture" | ShareControl | "complete" | "exit";

export const SHARE_SEQUENCE = Object.freeze({
  headingEnd: 1000,
  readingEnd: 3800,
  openingEnd: 5700,
  jumpEnd: 7400,
  settlingEnd: 10500,
  controlsStart: 11200,
  controlDuration: 1100,
  complete: 17800,
});

export interface ShareSequenceFrame {
  readonly phase: ShareSequencePhase;
  readonly elapsedMs: number;
  readonly headingPresence: number;
  readonly reasonsPresence: number;
  readonly readProgress: number;
  readonly modalPresence: number;
  readonly cubeTransfer: number;
  readonly lampsTransfer: number;
  readonly captureStill: boolean;
  readonly captureFlash: number;
  readonly activeControl: { readonly id: ShareControl; readonly hover: number; readonly press: number } | null;
  readonly exitProgress: number;
}

const clamp = (value: number): number => Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
const smooth = (value: number): number => { const t = clamp(value); return t * t * (3 - 2 * t); };
const range = (time: number, from: number, to: number): number => smooth((time - from) / (to - from));

/** One director-owned clock. No scroll interpolation, timers, or new world objects. */
export function sampleShareSequence(elapsedMs: number, motion: MarketingMotion, exitProgress = 0): ShareSequenceFrame {
  const t = motion === "reduced" ? SHARE_SEQUENCE.complete
    : Math.max(0, Number.isFinite(elapsedMs) ? elapsedMs : 0);
  const exit = clamp(exitProgress);
  const controlIndex = Math.floor((t - SHARE_SEQUENCE.controlsStart) / SHARE_SEQUENCE.controlDuration);
  const control = SHARE_CONTROL_ORDER[controlIndex];
  const local = (t - SHARE_SEQUENCE.controlsStart) % SHARE_SEQUENCE.controlDuration;
  const phase: ShareSequencePhase = exit > 0 ? "exit"
    : t < SHARE_SEQUENCE.headingEnd ? "heading"
      : t < SHARE_SEQUENCE.readingEnd ? "reading"
        : t < SHARE_SEQUENCE.openingEnd ? "opening"
          : t < SHARE_SEQUENCE.jumpEnd ? "jump"
            : t < SHARE_SEQUENCE.settlingEnd ? "settling"
              : t < SHARE_SEQUENCE.controlsStart ? "capture"
                : control ?? "complete";
  return {
    phase,
    elapsedMs: t,
    headingPresence: range(t, 0, SHARE_SEQUENCE.headingEnd),
    reasonsPresence: range(t, SHARE_SEQUENCE.readingEnd, SHARE_SEQUENCE.openingEnd),
    readProgress: range(t, SHARE_SEQUENCE.headingEnd, SHARE_SEQUENCE.readingEnd),
    modalPresence: range(t, SHARE_SEQUENCE.readingEnd, SHARE_SEQUENCE.openingEnd),
    cubeTransfer: range(t, SHARE_SEQUENCE.openingEnd, SHARE_SEQUENCE.jumpEnd) * (1 - exit),
    lampsTransfer: range(t, SHARE_SEQUENCE.jumpEnd, SHARE_SEQUENCE.settlingEnd-500) * (1 - exit),
    captureStill: t >= SHARE_SEQUENCE.settlingEnd && exit === 0,
    captureFlash: motion === 'reduced' || exit > 0 ? 0 : range(t,SHARE_SEQUENCE.settlingEnd,SHARE_SEQUENCE.settlingEnd+50)*(1-range(t,SHARE_SEQUENCE.settlingEnd+270,SHARE_SEQUENCE.settlingEnd+630)),
    activeControl: control && exit === 0 ? {
      id: control,
      hover: range(local, 0, 180) * (1 - range(local, 860, 1100)),
      press: 0,
    } : null,
    exitProgress: exit,
  };
}
